// Honest feedback band — last section before the footer. Anonymous, one-tap.
import React, { useState } from 'react';
import axios from 'axios';
import { Meh, Smile, Laugh, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RATINGS = [
  { value: 4, Icon: Laugh, label: 'Loved it', color: '#2dd4bf' },
  { value: 3, Icon: Smile, label: 'Helped', color: '#34d399' },
  { value: 2, Icon: Meh, label: 'Okay', color: '#fbbf24' },
];

const FeedbackBand = () => {
  const [picked, setPicked] = useState(null);
  const [fid, setFid] = useState(null);
  const [message, setMessage] = useState('');
  const [done, setDone] = useState(false);

  const pick = async (value) => {
    setPicked(value);
    try {
      const { data } = await axios.post(`${API}/feedback`, { rating: value, page: 'home' });
      setFid(data.id);
    } catch (e) {
      console.error(e);
    }
  };

  const send = async () => {
    const msg = message.trim();
    if (msg && fid) {
      try {
        await axios.post(`${API}/feedback/${fid}/message`, { message: msg });
      } catch (e) {
        console.error(e);
      }
    }
    setDone(true);
  };

  return (
    <section
      className="py-14 md:py-16 px-4 md:px-6 border-b border-white/10"
      style={{ background: '#0F172A' }}
      data-testid="feedback-band"
    >
      <div className="max-w-2xl mx-auto text-center">
        {done ? (
          <p className="text-white text-lg font-semibold" data-testid="feedback-thanks">
            Thank you — a real human reads every one of these. <span aria-hidden>🤍</span>
          </p>
        ) : (
          <>
            <h2 className="text-xl md:text-2xl font-bold text-white tracking-tight" data-testid="feedback-headline">
              Did this page help you?
            </h2>
            <p className="text-sm text-gray-400 mt-2">Be honest — we build from your answers.</p>

            <div className="mt-6 flex items-center justify-center gap-3 sm:gap-4">
              {RATINGS.map(({ value, Icon, label, color }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => pick(value)}
                  data-testid={`feedback-rating-${value}`}
                  className={`flex flex-col items-center gap-1.5 min-w-[64px] px-3 py-2.5 rounded-2xl border transition-all duration-300 ${
                    picked === value
                      ? 'bg-white/10 scale-105'
                      : 'bg-white/5 border-white/10 hover:bg-white/10'
                  }`}
                  style={picked === value ? { borderColor: color } : undefined}
                >
                  <Icon size={26} style={{ color }} strokeWidth={1.8} />
                  <span className="text-[11px] font-semibold text-gray-300">{label}</span>
                </button>
              ))}
            </div>

            {picked !== null && (
              <div className="mt-6 flex flex-col sm:flex-row items-stretch gap-3 max-w-lg mx-auto">
                <input
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && send()}
                  maxLength={1000}
                  placeholder="What's missing or confusing? (optional)"
                  className="flex-1 bg-white/5 border border-white/15 rounded-full px-5 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-teal-400"
                  data-testid="feedback-message-input"
                />
                <button
                  type="button"
                  onClick={send}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-teal-600 hover:bg-teal-500 text-white text-sm font-bold transition-colors"
                  data-testid="feedback-send-btn"
                >
                  Send <Send size={14} />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default FeedbackBand;
